import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';

import { PartnerCustomPackageRequest } from 'vopay';
import vopayApi from '../../services/api.config';
import { useDataFetchIndicator } from '..';
import { NewBillingPackageData } from '../../components/BillingPackages/types';

export type CustomPackageResponseValue = {
  success: boolean | null;
  errorMessage: string;
  isApproved: boolean;
};

type HookProps = {
  api: vp.Api;
};

type ReturnTuple = [CustomPackageResponseValue, (params: NewBillingPackageData) => void, () => void];

function usePostCustomPackage({ api }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<NewBillingPackageData | null>(null);
  const [response, setResponse] = useState<CustomPackageResponseValue>({
    success: null,
    errorMessage: '',
    isApproved: false,
  });
  const [shouldSendRequest, setSendRequest] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  useEffect(() => {
    const sendRequest = async (packageData: NewBillingPackageData) => {
      setDataFetchingInfo(true, 'submitting custom billing package', 'fullScreen');

      const { Success, ErrorMessage, IsApproved } = await api.postPartnerCustomPackage(
        buildRequestParams(packageData),
        source,
      );

      setResponse({ success: Success, errorMessage: ErrorMessage, isApproved: IsApproved });
      setSendRequest(false);
      setDataFetchingInfo(false);
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (_params: NewBillingPackageData) => {
      setParams(_params);
      setSendRequest(true);
    },
    () => {
      setParams(null);
      setResponse({ success: null, errorMessage: '', isApproved: false });
      setSendRequest(false);
    },
  ];
}

export function buildRequestParams(data: NewBillingPackageData): PartnerCustomPackageRequest {
  const packageData = {
    PackageName: data.packageName,
    GCMEnabled: data.gcmEnabled,
    SubAccountPermissions: data.subAccountsEnabled,
    ClientAccountsEnabled: data.clientAccountsEnabled,
    AutoBalanceTransferEnabled: data.autoBalanceTransferEnabled,
    MaxGCMAmount: vp.sanitizeToNumeric(data.maxGcmAmount),
    SetupFee: vp.sanitizeToNumeric(data.setupFee),
    Country: data.country,
    TermInMonths: data.termInMonths,
  };

  const billingData = {
    Currency: data.currency,
    PlanType: data.planType,
    BillingMethod: data.billingMethod,
    InvoiceDue: data.invoiceDue,
    InvoiceDueDate: data.invoiceDueDate,
    PlatformLicenceFee: vp.sanitizeToNumeric(data.platformLicenseFee),
    MinimumProgramFee: vp.sanitizeToNumeric(data.minProgramFee),
    NoPlatformLicenceFeePeriod: data.noPlatformLicenseFee,
    NoMinimumProgramFeePeriod: data.noMinProgramFeePeriod,
    UseProgressivePricing: data.useProgressivePricing,
    BlendedReturnRatesEnabled: data.blendedReturnRatesEnabled,
    PlatformLicenseFeeFrequency: data.platformLicenseFeeFrequency,
  };

  const subscriptionData = {
    ...data,
  };

  const requestData = { ...packageData, ...billingData, ...subscriptionData };

  return Object.entries(requestData).reduce(
    (acc, [key, value]) => (value ? { ...acc, [key]: value } : acc),
    {} as PartnerCustomPackageRequest,
  );
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(usePostCustomPackage);
